import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Gestión de productos";
const heroImage = "https://net-basis.net/Bilder/ict-product-management-market-adaptation-modular-development.webp";
const heroAlt = "Unas manos presentan un cubo modular con bloques rojos para gestión de productos TIC.";
const heroClaimLine1 = "Perfectamente adaptado!";
const heroClaimLine2 = "Posicionado con éxito!";
const widget2 = "Su socio experimentado para una gestión de productos adaptada al mercado y la adaptación local de productos en la región DACH. Apoyamos a los fabricantes extranjeros en la armonización precisa de sus soluciones tecnológicas con los requisitos del mercado de habla alemana.";
const benefitsTitle = "Cómo se beneficia de nuestra experiencia en __gestión de productos__:";
const p1 = "La introducción y gestión de productos en nuevos mercados plantea desafíos complejos a las empresas. Los diferentes requisitos normativos, las situaciones competitivas locales y las cambiantes necesidades de los clientes exigen un conocimiento profundo de los mercados objetivos. Sin una sólida experiencia local y una competencia contrastada en gestión de productos sobre el terreno, las empresas se arriesgan a realizar inversiones erróneas, perder oportunidades de mercado y hacer un uso ineficiente de los recursos.";
const p2Text = "Por lo tanto, NetBasis International interviene exactamente aquí y ofrece a sus fabricantes socios una amplia experiencia: Asumimos la gestión integral de productos en la región DACH, desde el análisis de mercado y la adaptación de productos hasta el soporte continuo y la optimización de su cartera de productos.";
const p2Parts = p2Text.split(COMPANY_NAME);
const p3 = "Desde Düsseldorf, nos aseguramos de que sus productos se adapten de forma óptima a los requisitos locales. Coordinamos todos los procesos relacionados con los productos, supervisamos el cumplimiento de las normativas legales y garantizamos una comunicación eficiente entre su oficina central y los agentes de la región comercial. De este modo, estará posicionado de forma competitiva desde el primer día.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Gestión profesional de productos sin necesidad de crear estructuras propias sobre el terreno",
  "Adaptación de productos al mercado conforme a los requisitos y normas locales",
  "Observación continua del mercado y análisis de la competencia",
  "Gestión eficiente del ciclo de vida de toda su cartera de productos",
  "Optimización de las cadenas de suministro y de los procesos de venta",
  "Garantía del cumplimiento normativo en la región DACH",
  "Liberación de su oficina central de las tareas operativas de gestión de productos",
  "Acceso a redes locales, proveedores de servicios y canales de distribución",
  "Rápida capacidad de respuesta ante los cambios del mercado y los comentarios de los clientes",
  "Solución escalable: adaptable de forma flexible a sus objetivos de crecimiento",
];

export default function GestionDeProductosEsPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Gestión de Productos | NetBasis International"
        description="Adaptación y posicionamiento específico de sus productos para los requisitos particulares de los mercados objetivos DACH."
        ogUrl="https://net-basis.net/es/servicios/desarrollo-de-negocio/gestion-de-productos"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-center overflow-hidden pointer-events-none pt-12 sm:pt-16 md:pt-24 lg:pt-20">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center px-4 leading-[1.15] md:leading-[1.5] pm-hero-text-slide"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  <span className="block sm:inline">{heroClaimLine1}</span>
                  <span className="hidden sm:inline"> </span>
                  <span className="block sm:inline">{heroClaimLine2}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="text-foreground-950 font-semibold text-sm md:text-base mb-4 leading-relaxed">
              {widget2}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-base">
              <p>
                {(() => {
                  const parts = p1.split(COMPANY_NAME);
                  return parts.map((part, i) => (
                    <span key={i}>
                      {part}
                      {i < parts.length - 1 && (
                        <strong className="text-primary-600">{COMPANY_NAME}</strong>
                      )}
                    </span>
                  ));
                })()}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>{p3}</p>
            </div>

            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i} className="mr-[0.15em]">{part}</em> : part
              )}
            </h2>

            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}